/**
 * Route Royal Page Table of Contents Component
 * 
 * Clean navigation sidebar for long pages
 */

'use client';

import type { PageTableOfContentsProps } from '@routeroyal/theme-core';

/**
 * RouteRoyalPageTableOfContents - Route Royal-themed navigation sidebar
 * 
 * Displays a list of headings extracted from the page content,
 * with active heading highlighting and clean blue styling.
 */
export function RouteRoyalPageTableOfContents({
  headings,
  activeId,
  className = '',
  title = 'On this page',
}: PageTableOfContentsProps) {
  if (headings.length === 0) return null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      window.history.pushState(null, '', `#${id}`);
    }
  };

  return (
    <nav
      className={`bg-gray-50 rounded-lg p-5 border border-gray-200 ${className}`}
      aria-label="Table of contents"
    >
      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">
        {title}
      </h3>
      <ul className="space-y-1">
        {headings.map(({ id, text, level }) => (
          <li
            key={id}
            style={{ paddingLeft: `${(level - 2) * 12}px` }}
          >
            <a
              href={`#${id}`}
              onClick={(e) => handleClick(e, id)}
              className={`
                block text-sm py-1.5 px-3 rounded transition-colors
                ${activeId === id
                  ? 'text-blue-700 font-semibold bg-blue-50 border-l-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
